import { createContext, useContext, useState } from 'react';
import { ContextStatus, ECreditType, PlanFeaturesType, EProductType, currencyAsString, Currency, CostTypeRef } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../auth/AuthenticationContext';
import updatePlan from '../../../../domain/use_cases/plans/updatePlan';
import deletePlan from '../../../../domain/use_cases/plans/deletePlan';
import { ICreditMap, ILeedbucksRatesTemp, IPlan, IPlanLink, IPlanPrice, IPlanStartingPeriod } from '@leedpulse-monorepo/interfaces';
import syncPlanWithStripe from '../../../../domain/use_cases/plans/syncPlanWithStripe';
import { IExtendedPlan } from '../../data/PlansContext';

export interface PlanEditionFormContextProps {
  currentId: string;
  wipPlan: IExtendedPlan | null;
  savedPlan: IPlan | null; // last saved version, tells what is not saved yet
  updateStatus: ContextStatus;
  deleteStatus: ContextStatus;
  syncStripeStatus: ContextStatus;
  initWithPlan: (_: IPlan) => void;
  submitUpdate: () => Promise<void>;
  submitDelete: () => Promise<void>;
  submitSyncStripe: () => Promise<void>;
  onIdChange: (_: string) => void;
  onColorChange: (_: string) => void;
  onCreditsChange: (_: ECreditType, __: number) => void;
  onFeaturesChange: (_: PlanFeaturesType, __: boolean) => void;
  onNameChange: (_: string) => void;
  onPriceChange: (_: Partial<IPlanPrice>) => void;
  onProductChange: (_: EProductType) => void;
  onRecurrenceChange: (_: boolean) => void;
  onRecurrenceCreditsResetChange: (_: boolean) => void;
  onArchivedChange: (_: boolean) => void;
  onLinksChange: (_: IPlanLink[]) => void;
  onDowngradableToChange: (_: string[]) => void;
  onUpgradableToChange: (_: string[]) => void;
  onPaygChange: (_: ECreditType, __: number) => void;
  onClearCredit: (_: ECreditType) => void;
  onClearPayg: (_: ECreditType) => void;
  onPartnerSystemsChange: (_: string[]) => void;
  onLeedbucksRatesChange: (_: Partial<Record<CostTypeRef, { unit_value: number; }>>) => void;
  onLeedbucksRatesTempChange: (_: ILeedbucksRatesTemp[]) => void;
  onStartingPeriodChange: (_: IPlanStartingPeriod | undefined) => void;
  onNoCommitmentChange: (_: boolean) => void;
}

const PlanEditionFormContext = createContext<PlanEditionFormContextProps>({
  currentId: '',
  wipPlan: null as IExtendedPlan | null,
  savedPlan: null as IPlan | null,
  updateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
  syncStripeStatus: ContextStatus.IDLE,
  initWithPlan: (_: IPlan) => { return; },
  submitUpdate: async () => { return Promise.resolve(); },
  submitDelete: async () => { return Promise.resolve(); },
  submitSyncStripe: async () => { return Promise.resolve(); },
  onIdChange: (_: string) => { return; },
  onColorChange: (_: string) => { return; },
  onCreditsChange: (_: ECreditType, __: number) => { return; },
  onFeaturesChange: (_: PlanFeaturesType, __: boolean) => { return; },
  onNameChange: (_: string) => { return; },
  onPriceChange: (_: Partial<IPlanPrice>) => { return; },
  onProductChange: (_: EProductType) => { return; },
  onRecurrenceChange: (_: boolean) => { return; },
  onRecurrenceCreditsResetChange: (_: boolean) => { return; },
  onArchivedChange: (_: boolean) => { return; },
  onLinksChange: (_: IPlanLink[]) => { return; },
  onDowngradableToChange: (_: string[]) => { return; },
  onUpgradableToChange: (_: string[]) => { return; },
  onPaygChange: (_: ECreditType, __: number) => { return; },
  onClearCredit: (_: ECreditType) => { return; },
  onClearPayg: (_: ECreditType) => { return; },
  onPartnerSystemsChange: (_: string[]) => { return; },
  onLeedbucksRatesChange: (_: Partial<Record<CostTypeRef, { unit_value: number; }>>) => { return; },
  onLeedbucksRatesTempChange: (_: ILeedbucksRatesTemp[]) => { return; },
  onStartingPeriodChange: (_: IPlanStartingPeriod | undefined) => { return; },
  onNoCommitmentChange: (_: boolean) => { return; },
});

export const PlanEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [currentId, setCurrentId] = useState('');
  const [wipPlan, setWipPlan] = useState(null as IExtendedPlan | null);
  const [savedPlan, setSavedPlan] = useState(null as IPlan | null);
  const [clearedCredits, setClearedCredits] = useState<ECreditType[]>([]);
  const [clearedPayg, setClearedPayg] = useState<ECreditType[]>([]);

  const [updateStatus, setUpdateStatus] = useState(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState(ContextStatus.IDLE);
  const [syncStripeStatus, setSyncStripeStatus] = useState(ContextStatus.IDLE);
  const authContext = useContext(AuthenticationContext);

  const initWithPlan = async (plan: IPlan) => {
    setCurrentId(plan.id);
    setWipPlan(plan);
    setSavedPlan(plan);
    setClearedCredits([]);
    setClearedPayg([]);
  };

  const submitUpdate = async () => {
    if (wipPlan === null || authContext.authentication === null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      await updatePlan(currentId, wipPlan, authContext.authentication, { clearedCredits, clearedPayg });
      setClearedCredits([]);
      setClearedPayg([]);
      setSavedPlan(wipPlan);
      setUpdateStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async () => {
    if (wipPlan === null || authContext.authentication === null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deletePlan(wipPlan, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const submitSyncStripe = async () => {
    if (wipPlan === null || authContext.authentication === null) return;
    setSyncStripeStatus(ContextStatus.LOADING);
    try {
      const syncedPlan = await syncPlanWithStripe(wipPlan, authContext.authentication);
      if (syncedPlan) {
        setWipPlan(o =>
          o ?
            {
              ...o,
              ...syncedPlan
            } :
            { ...syncedPlan });
      }
      setSyncStripeStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setSyncStripeStatus(ContextStatus.ERROR);
    }
  };

  const onIdChange = (id: string) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, id }
    );
  };

  const onColorChange = (color: string) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, color }
    );
  };

  const onCreditsChange = (creditType: ECreditType, value: number) => {
    if (wipPlan === null) return;
    setWipPlan(
      {
        ...wipPlan,
        credits: {
          ...wipPlan.credits,
          [creditType]: value,
        }
      }
    );
  };

  const onClearCredit = (creditType: ECreditType) => {
    if (wipPlan === null) return;
    const { [creditType]: _removed, ...rest } = wipPlan.credits as ICreditMap;
    setWipPlan({ ...wipPlan, credits: rest as ICreditMap });
    setClearedCredits((prev) => (prev.includes(creditType) ? prev : [...prev, creditType]));
  };

  const onClearPayg = (creditType: ECreditType) => {
    if (wipPlan === null || !wipPlan.payg) return;
    const { [creditType]: _removed, ...rest } = wipPlan.payg as Partial<ICreditMap>;
    setWipPlan({ ...wipPlan, payg: rest });
    setClearedPayg((prev) => (prev.includes(creditType) ? prev : [...prev, creditType]));
  };

  const onFeaturesChange = (featureType: PlanFeaturesType, value: boolean) => {
    if (wipPlan === null) return;
    setWipPlan(
      {
        ...wipPlan,
        features: {
          ...wipPlan.features,
          [featureType]: value,
        }
      }
    );
  };

  const onNameChange = (name: string) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, name }
    );
  };

  const onPriceChange = (changes: Partial<IPlanPrice>) => {
    if (wipPlan === null) return;


    setWipPlan(o => {
      if (changes.formatted === undefined)
        changes.formatted = `${changes.value?.toFixed(2) ?? o?.price.value.toFixed(2)} ${currencyAsString(changes.currency ?? o?.price.currency ?? Currency.EUR)}`;

      return {
        ...wipPlan,
        price: {
          ...wipPlan.price,
          ...changes
        }
      }
    });
  };

  const onProductChange = (product: EProductType) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, product }
    );
  };

  const onRecurrenceChange = (recurrence: boolean) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, recurrence }
    );
  };

  const onRecurrenceCreditsResetChange = (recurrence_credits_reset: boolean) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, recurrence_credits_reset }
    );
  };

  const onArchivedChange = (archived: boolean) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, archived }
    );
  };

  const onLinksChange = (links: IPlanLink[]) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, links }
    );
  };

  const onDowngradableToChange = (downgradable_to: string[]) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, downgradable_to }
    );
  };

  const onUpgradableToChange = (upgradable_to: string[]) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, upgradable_to }
    );
  };

  const onPaygChange = (creditType: ECreditType, value: number) => {
    if (wipPlan === null) return;
    setWipPlan(
      {
        ...wipPlan,
        payg: {
          ...wipPlan.payg,
          [creditType]: value,
        } as ICreditMap
      }
    );
  };

  const onPartnerSystemsChange = (partner_systems: string[]) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, partner_systems }
    );
  };

  const onLeedbucksRatesChange = (leedbucks_rates: Partial<Record<CostTypeRef, { unit_value: number; }>>) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, leedbucks_rates }
    );
  };

  const onLeedbucksRatesTempChange = (leedbucks_rates_temp: ILeedbucksRatesTemp[]) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, leedbucks_rates_temp }
    );
  };

  const onStartingPeriodChange = (starting_period: IPlanStartingPeriod | undefined) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, starting_period }
    );
  };

  const onNoCommitmentChange = (no_commitment: boolean) => {
    if (wipPlan === null) return;
    setWipPlan(
      { ...wipPlan, no_commitment }
    );
  };

  return (
    <PlanEditionFormContext.Provider
      value={{
        currentId,
        wipPlan,
        savedPlan,
        updateStatus,
        deleteStatus,
        syncStripeStatus,
        initWithPlan,
        submitUpdate,
        submitDelete,
        submitSyncStripe,
        onIdChange,
        onColorChange,
        onCreditsChange,
        onFeaturesChange,
        onNameChange,
        onPriceChange,
        onProductChange,
        onRecurrenceChange,
        onRecurrenceCreditsResetChange,
        onArchivedChange,
        onLinksChange,
        onDowngradableToChange,
        onUpgradableToChange,
        onPaygChange,
        onClearCredit,
        onClearPayg,
        onPartnerSystemsChange,
        onLeedbucksRatesChange,
        onLeedbucksRatesTempChange,
        onStartingPeriodChange,
        onNoCommitmentChange,
      }}
    >
      {children}
    </PlanEditionFormContext.Provider>
  );
};

export default PlanEditionFormContext;
